import * as React from 'react';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Alert from '@mui/joy/Alert';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Input from '@mui/joy/Input';
import Modal from '@mui/joy/Modal';
import ModalClose from '@mui/joy/ModalClose';
import ModalDialog from '@mui/joy/ModalDialog';
import Option from '@mui/joy/Option';
import Radio from '@mui/joy/Radio';
import RadioGroup from '@mui/joy/RadioGroup';
import Select from '@mui/joy/Select';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Table from '@mui/joy/Table';
import Textarea from '@mui/joy/Textarea';
import Typography from '@mui/joy/Typography';
import { EditRounded, FavoriteRounded, ReceiptLong } from '@mui/icons-material';

import Header from '../components/common/Header';
import Sidebar from '../components/common/Sidebar';
import PageBreadcrumbs from '../components/common/PageBreadcrumbs';
import {
  fetchDonationForEdit,
  fetchNityaSevaEnrollmentDetail,
  NityaSevaDonationHistoryItem,
  NityaSevaEnrollmentDetailResponse,
  NityaSevaPaymentFrequency,
  recordNityaSevaManualDonation,
  updateDonationById,
  updateNityaSevaEnrollment,
} from '../api';

const modeLabel: Record<string, string> = {
  ecs_nach: 'ECS / NACH',
  auto_razorpay: 'Auto Razorpay',
  online_transfer: 'Online Transfer',
  cash_first_sunday: 'Cash 1st Sunday',
};

const enrollmentSourceLabel = (value?: string) => (
  value === 'offline' ? 'Offline · Added by admin' : 'Online · Self-enrolled'
);

const statusColor = (status?: string) => {
  const value = (status || '').toLowerCase();
  if (value === 'due' || value === 'unpaid' || value.includes('not paid')) return 'danger';
  if (value === 'success' || value === 'paid' || value === 'captured' || value === 'verified') return 'success';
  if (value === 'pending') return 'warning';
  if (value === 'failed') return 'danger';
  return 'neutral';
};

type SortDirection = 'asc' | 'desc';
type HistorySortKey = 'month' | 'date' | 'amount' | 'status' | 'method' | 'source' | 'donationId' | 'transactionId' | 'note';

const compareText = (a?: string | number | null, b?: string | number | null) => (
  String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true, sensitivity: 'base' })
);

const compareNumbers = (a?: number | string | null, b?: number | string | null) => (
  Number(a ?? 0) - Number(b ?? 0)
);

const compareDates = (a?: string | null, b?: string | null) => {
  const first = a ? dayjs(a).valueOf() : 0;
  const second = b ? dayjs(b).valueOf() : 0;
  return first - second;
};

const applySortDirection = (value: number, direction: SortDirection) => (
  direction === 'asc' ? value : -value
);

const renderSortableHeader = (
  label: string,
  active: boolean,
  direction: SortDirection,
  onClick: () => void,
) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    sx={{
      all: 'unset',
      cursor: 'pointer',
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.4,
      color: active ? 'primary.600' : 'inherit',
      font: 'inherit',
      fontWeight: 700,
      '&:hover': { color: 'primary.500' },
    }}
  >
    <span>{label}</span>
    <Box component="span" aria-hidden="true" sx={{ fontSize: '0.8rem' }}>
      {active ? (direction === 'asc' ? '↑' : '↓') : '↕'}
    </Box>
  </Box>
);

const paymentFrequencyOptions: Array<{ value: NityaSevaPaymentFrequency; label: string; months: number }> = [
  { value: 'monthly', label: 'Monthly', months: 1 },
  { value: 'quarterly', label: 'Quarterly', months: 3 },
  { value: 'half_yearly', label: 'Half-Yearly', months: 6 },
  { value: 'annually', label: 'Annually', months: 12 },
];

const getPaymentFrequencyMonths = (value: NityaSevaPaymentFrequency) => paymentFrequencyOptions.find((option) => option.value === value)?.months || 1;
const getPaymentFrequencyLabel = (value: NityaSevaPaymentFrequency) => paymentFrequencyOptions.find((option) => option.value === value)?.label || 'Monthly';
const getCycleAmount = (monthlyAmount: number, value: NityaSevaPaymentFrequency) => Number(monthlyAmount || 0) * getPaymentFrequencyMonths(value);
const formatFullAddress = (enrollment?: NityaSevaEnrollmentDetailResponse['enrollment']) => {
  if (!enrollment) {
    return '—';
  }
  const baseAddress = String(enrollment.address || '').trim();
  const normalizedBase = baseAddress.toLowerCase();
  const extras = [enrollment.city, enrollment.state, enrollment.pincode, enrollment.country]
    .map((part) => String(part || '').trim())
    .filter((part) => part && !normalizedBase.includes(part.toLowerCase()));
  return [baseAddress, ...extras].filter(Boolean).join(', ') || '—';
};
const getPaymentFrequencyFromMonths = (months: number): NityaSevaPaymentFrequency => {
  if (months >= 12) return 'annually';
  if (months >= 6) return 'half_yearly';
  if (months >= 3) return 'quarterly';
  return 'monthly';
};
const parseNityaSevaCoverageMeta = (value?: string) => {
  const match = String(value || '').match(/Nitya Seva Coverage:\s*(\d{4}-\d{2})\|(\d+)/i);
  if (!match) {
    return null;
  }
  return {
    forMonth: match[1],
    frequency: getPaymentFrequencyFromMonths(Number(match[2] || 1)),
  };
};
const buildNityaSevaCoverageMeta = (forMonth: string, frequency: NityaSevaPaymentFrequency) => `Nitya Seva Coverage: ${forMonth}|${getPaymentFrequencyMonths(frequency)}`;

export default function NityaSevaDetails() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const enrollmentId = searchParams.get('id');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [detailData, setDetailData] = React.useState<NityaSevaEnrollmentDetailResponse | null>(null);
  const [manualTargetRow, setManualTargetRow] = React.useState<NityaSevaDonationHistoryItem | null>(null);
  const [manualMode, setManualMode] = React.useState<'create' | 'edit'>('create');
  const [editingDonationSnapshot, setEditingDonationSnapshot] = React.useState<Record<string, any> | null>(null);
  const [savingManualDonation, setSavingManualDonation] = React.useState(false);
  const [manualAmount, setManualAmount] = React.useState('1100');
  const [manualDate, setManualDate] = React.useState(dayjs().format('YYYY-MM-DD'));
  const [manualForMonth, setManualForMonth] = React.useState(dayjs().format('YYYY-MM'));
  const [manualPaymentFrequency, setManualPaymentFrequency] = React.useState<NityaSevaPaymentFrequency>('monthly');
  const [manualTransactionId, setManualTransactionId] = React.useState('');
  const [manualMethod, setManualMethod] = React.useState('Cash');
  const [manualNote, setManualNote] = React.useState('');
  const [paymentFilter, setPaymentFilter] = React.useState('');
  const [historySort, setHistorySort] = React.useState<{ key: HistorySortKey; direction: SortDirection }>({ key: 'month', direction: 'desc' });

  const normalizeDueNote = (note?: string) => {
    const trimmed = (note || '').trim();
    if (!trimmed) {
      return '—';
    }
    const lower = trimmed.toLowerCase();
    if (lower.includes('no settled nitya seva donation')) {
      return '—';
    }
    return trimmed;
  };

  const handleHistorySort = React.useCallback((key: HistorySortKey) => {
    setHistorySort((prev) => (
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    ));
  }, []);

  const filteredSortedHistory = React.useMemo(() => {
    const next = Array.isArray(detailData?.donationHistory) ? [...detailData.donationHistory] : [];

    const filtered = next.filter((row) => {
      if (paymentFilter === 'paid') {
        return row.kind !== 'due';
      }
      if (paymentFilter === 'unpaid') {
        return row.kind === 'due';
      }
      return true;
    });

    filtered.sort((a, b) => {
      const statusA = a.kind === 'due' ? 'Due' : (a.donation?.status || a.status || '—');
      const statusB = b.kind === 'due' ? 'Due' : (b.donation?.status || b.status || '—');
      const methodA = a.kind === 'due' ? '—' : (a.donation?.paymentMethod || a.paymentMethod || '—');
      const methodB = b.kind === 'due' ? '—' : (b.donation?.paymentMethod || b.paymentMethod || '—');
      const sourceA = a.kind === 'due' ? 'Due' : (a.subscription?.id ? 'Subscription' : 'Manual');
      const sourceB = b.kind === 'due' ? 'Due' : (b.subscription?.id ? 'Subscription' : 'Manual');
      const noteA = a.kind === 'due' ? normalizeDueNote(a.dueReason) : (a.donation?.note || '—');
      const noteB = b.kind === 'due' ? normalizeDueNote(b.dueReason) : (b.donation?.note || '—');
      let result = 0;

      switch (historySort.key) {
        case 'month':
          result = compareDates(a.month || a.donation?.date || '', b.month || b.donation?.date || '');
          break;
        case 'date':
          result = compareDates(a.displayDate || a.donation?.date || '', b.displayDate || b.donation?.date || '');
          break;
        case 'amount':
          result = compareNumbers(a.amount || a.donation?.amount || 0, b.amount || b.donation?.amount || 0);
          break;
        case 'status':
          result = compareText(statusA, statusB);
          break;
        case 'method':
          result = compareText(methodA, methodB);
          break;
        case 'source':
          result = compareText(sourceA, sourceB);
          break;
        case 'donationId':
          result = compareNumbers(a.donation?.id || 0, b.donation?.id || 0);
          break;
        case 'transactionId':
          result = compareText(a.donation?.txnRefId || a.subscription?.razorpayId || '—', b.donation?.txnRefId || b.subscription?.razorpayId || '—');
          break;
        case 'note':
          result = compareText(noteA, noteB);
          break;
        default:
          result = 0;
      }

      return applySortDirection(result, historySort.direction);
    });

    return filtered;
  }, [detailData?.donationHistory, historySort, paymentFilter]);

  const downloadReceipt = async (donationId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_API_URL}/api/donation/receipt/${donationId}`, {
        method: 'GET',
        credentials: 'include',
      });
      if (!response.ok) {
        throw new Error('Failed to download receipt');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `donation-receipt-${donationId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading receipt:', err);
    }
  };

  const closeManualDonationModal = React.useCallback(() => {
    setManualTargetRow(null);
    setManualMode('create');
    setEditingDonationSnapshot(null);
  }, []);

  const openRecordDonationModal = (row: NityaSevaDonationHistoryItem) => {
    const defaultFrequency = detailData?.enrollment.paymentFrequency || 'monthly';
    setManualMode('create');
    setEditingDonationSnapshot(null);
    setManualPaymentFrequency(defaultFrequency);
    setManualAmount(String(getCycleAmount(Number(detailData?.enrollment.pledgeAmount || row.amount || 0), defaultFrequency)));
    setManualDate(dayjs().format('YYYY-MM-DD'));
    setManualForMonth(row.month ? dayjs(row.month).format('YYYY-MM') : dayjs().format('YYYY-MM'));
    setManualTransactionId('');
    setManualNote('');
    setManualMethod(detailData?.enrollment.submissionMode === 'cash_first_sunday' ? 'Cash' : 'Online');
    setManualTargetRow(row);
  };

  const openEditDonationModal = async (row: NityaSevaDonationHistoryItem) => {
    if (!row.donation?.id) {
      return;
    }
    setSavingManualDonation(true);
    setError('');
    try {
      const donation = await fetchDonationForEdit(row.donation.id);
      const coverage = parseNityaSevaCoverageMeta(String(donation?.subPurpose || ''));
      const fallbackFrequency = detailData?.enrollment.paymentFrequency || 'monthly';
      setManualMode('edit');
      setEditingDonationSnapshot(donation);
      setManualPaymentFrequency(coverage?.frequency || fallbackFrequency);
      setManualAmount(String(Number(donation?.amount || row.donation?.amount || 0)));
      setManualDate(donation?.date ? dayjs(String(donation.date)).format('YYYY-MM-DD') : dayjs().format('YYYY-MM-DD'));
      setManualForMonth(coverage?.forMonth || (row.month ? dayjs(row.month).format('YYYY-MM') : dayjs().format('YYYY-MM')));
      setManualTransactionId(String(donation?.txnRefId || ''));
      setManualNote(String(donation?.note || ''));
      setManualMethod(String(donation?.paymentMethod || (detailData?.enrollment.submissionMode === 'cash_first_sunday' ? 'Cash' : 'Online')));
      setManualTargetRow(row);
    } catch (_err) {
      setError('Failed to load donation for editing. Please try again.');
    } finally {
      setSavingManualDonation(false);
    }
  };

  const loadDetail = React.useCallback(async () => {
    if (!enrollmentId) {
      setError('Missing enrollment id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const data = await fetchNityaSevaEnrollmentDetail(Number(enrollmentId));
      setDetailData(data);
    } catch (e) {
      setError('Failed to load Nitya Seva enrollment details.');
    } finally {
      setLoading(false);
    }
  }, [enrollmentId]);

  const refreshDetailAfterMutation = React.useCallback(async () => {
    await loadDetail();
    const followUps = [700, 1500, 2800];
    followUps.forEach((delayMs) => {
      window.setTimeout(() => {
        void loadDetail();
      }, delayMs);
    });
  }, [loadDetail]);

  const applyOptimisticManualDonation = React.useCallback((params: {
    amount: number;
    date: string;
    forMonth: string;
    paymentMethod: string;
    transactionId: string;
    note: string;
  }) => {
    setDetailData((prev) => {
      if (!prev) {
        return prev;
      }

      const dueMonth = dayjs(params.forMonth).format('YYYY-MM');
      const nextHistory = prev.donationHistory.filter((row) => {
        if (row.kind !== 'due' || !row.month) {
          return true;
        }
        return dayjs(row.month).format('YYYY-MM') !== dueMonth;
      });

      const optimisticRow: NityaSevaDonationHistoryItem = {
        kind: 'paid',
        month: params.forMonth,
        status: 'pending',
        displayDate: params.date,
        amount: params.amount,
        paymentMethod: params.paymentMethod,
        donation: {
          id: 0,
          date: params.date,
          amount: params.amount,
          paymentMethod: params.paymentMethod,
          status: 'pending',
          note: params.note,
          txnRefId: params.transactionId || undefined,
        },
      };

      return {
        ...prev,
        enrollment: {
          ...prev.enrollment,
          lastManualDonationAt: params.date,
          updatedAt: new Date().toISOString(),
        },
        donationHistory: [optimisticRow, ...nextHistory],
      };
    });
  }, []);

  React.useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh' }}>
      <Header />
      <Sidebar />
      <Box
        component="main"
        className="MainContent"
        sx={{
          px: { xs: 2, md: 6 },
          pt: { xs: 'calc(12px + var(--Header-height))', sm: 'calc(12px + var(--Header-height))', md: 3 },
          pb: { xs: 2, sm: 2, md: 3 },
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          gap: 1,
          bgcolor: 'background.body',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <PageBreadcrumbs
            links={[{ label: 'Nitya Seva', href: '/nitya-seva' }]}
            current={enrollmentId ? `Enrollment ${enrollmentId}` : 'Details'}
          />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }}>
          <Typography level="h2" component="h1">Nitya Seva Enrollment Details</Typography>
          <Button variant="soft" color="neutral" onClick={() => navigate('/nitya-seva')}>
            Back to Nitya Seva
          </Button>
        </Stack>

        {loading ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
            <CircularProgress size="sm" />
            <Typography level="body-sm">Loading details...</Typography>
          </Stack>
        ) : null}

        {error ? <Alert color="danger">{error}</Alert> : null}

        {detailData ? (
          <Stack spacing={2}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
              }}
            >
              <Card variant="soft">
                <CardContent>
                  <Typography level="title-sm">Donor</Typography>
                  <Typography>{detailData.enrollment.fullName}</Typography>
                  <Typography level="body-xs">WhatsApp: {detailData.enrollment.whatsAppNumber || '—'}</Typography>
                  <Typography level="body-xs">Phone: {detailData.enrollment.phone || '—'}</Typography>
                  <Typography level="body-xs">Email: {detailData.enrollment.email || '—'}</Typography>
                </CardContent>
              </Card>
              <Card variant="soft">
                <CardContent>
                  <Typography level="title-sm">Enrollment</Typography>
                  <Typography level="body-sm">Mode: {modeLabel[detailData.enrollment.submissionMode] || detailData.enrollment.submissionMode}</Typography>
                  <Typography level="body-sm">Enrolled via: {enrollmentSourceLabel(detailData.enrollment.enrollmentSource)}</Typography>
                  <Typography level="body-sm">Pledge: ₹{detailData.enrollment.pledgeAmount.toFixed(0)} ({detailData.enrollment.pledgeTier})</Typography>
                  <Typography level="body-sm">Status: {detailData.enrollment.status}</Typography>
                  <Typography level="body-xs">Created: {dayjs(detailData.enrollment.createdAt).format('D MMM YYYY')}</Typography>
                  <Typography level="body-xs">Updated: {dayjs(detailData.enrollment.updatedAt).format('D MMM YYYY')}</Typography>
                </CardContent>
              </Card>
              <Card variant="soft">
                <CardContent>
                  <Typography level="title-sm">Profile</Typography>
                  <Typography level="body-sm">DOB: {detailData.enrollment.dateOfBirth ? dayjs(detailData.enrollment.dateOfBirth).format('D MMM YYYY') : '—'}</Typography>
                  <Typography level="body-sm">Start Date: {detailData.enrollment.createdAt ? dayjs(detailData.enrollment.createdAt).format('D MMM YYYY') : '—'}</Typography>
                  <Typography level="body-sm">Anniversary: {detailData.enrollment.anniversaryDate ? dayjs(detailData.enrollment.anniversaryDate).format('D MMM YYYY') : '—'}</Typography>
                  <Typography level="body-sm">PAN: {detailData.enrollment.panNumber || '—'}</Typography>
                  <Typography level="body-sm">Last donation: {detailData.enrollment.lastManualDonationAt ? dayjs(detailData.enrollment.lastManualDonationAt).format('D MMM YYYY') : '—'}</Typography>
                </CardContent>
              </Card>
            </Box>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Address</Typography>
                <Typography level="body-sm">{formatFullAddress(detailData.enrollment)}</Typography>
                {detailData.enrollment.adminNote ? (
                  <>
                    <Typography level="title-sm" sx={{ mt: 2 }}>Admin Note</Typography>
                    <Typography level="body-sm">{detailData.enrollment.adminNote}</Typography>
                  </>
                ) : null}
              </CardContent>
            </Card>

            <Card variant="outlined">
              <CardContent>
                <Typography level="title-md" sx={{ mb: 1 }}>Donation History</Typography>
                <Alert color="warning" variant="soft" sx={{ mb: 1.5 }}>
                  Any active Nitya Seva month without a settled donation is shown here as due.
                </Alert>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1.5 }}>
                  <FormControl sx={{ minWidth: 220 }}>
                    <FormLabel>Donation Status</FormLabel>
                    <Select value={paymentFilter} onChange={(_, val) => setPaymentFilter((val as string) || '')} placeholder="Donation status">
                      <Option value="">All Donations</Option>
                      <Option value="paid">Paid</Option>
                      <Option value="unpaid">Unpaid</Option>
                    </Select>
                  </FormControl>
                </Stack>
                <Sheet
                  variant="outlined"
                  sx={{ borderRadius: 'lg', overflow: 'hidden', borderColor: 'divider', backgroundColor: 'background.surface' }}
                >
                  <Box sx={{ width: '100%', overflowX: 'auto' }}>
                    <Table
                      stickyHeader
                      hoverRow
                      sx={{
                        '--TableCell-headBackground': 'var(--joy-palette-background-level1)',
                        '--Table-headerUnderlineThickness': '1px',
                        '--TableCell-paddingY': '10px',
                        '--TableCell-paddingX': '14px',
                        tableLayout: 'fixed',
                        minWidth: 1290,
                        '& thead th': {
                          textTransform: 'uppercase',
                          fontSize: '0.7rem',
                          letterSpacing: 0.6,
                          color: 'text.secondary',
                          whiteSpace: 'nowrap',
                        },
                        '& tbody td': {
                          verticalAlign: 'middle',
                        },
                        '& thead th:last-child': {
                          position: 'sticky',
                          right: 0,
                          top: 0,
                          zIndex: 30,
                          backgroundColor: 'var(--joy-palette-background-level1)',
                          boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                        },
                        '& tbody tr:nth-of-type(even)': {
                          backgroundColor: 'var(--joy-palette-background-level1)',
                        },
                        '& tbody tr:hover': {
                          backgroundColor: 'var(--joy-palette-background-level2)',
                        },
                        '& tbody td:last-child': {
                          position: 'sticky',
                          right: 0,
                          zIndex: 20,
                          backgroundColor: 'background.surface',
                          boxShadow: '-1px 0 0 var(--joy-palette-divider)',
                        },
                        '& tbody tr:nth-of-type(even) td:last-child': {
                          backgroundColor: 'var(--joy-palette-background-level1)',
                        },
                        '& tbody tr:hover td:last-child': {
                          backgroundColor: 'var(--joy-palette-background-level2)',
                        },
                      }}
                    >
                      <colgroup>
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '130px' }} />
                        <col style={{ width: '100px' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '110px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '120px' }} />
                        <col style={{ width: '180px' }} />
                        <col style={{ width: '290px' }} />
                        <col style={{ width: '140px' }} />
                      </colgroup>
                      <thead>
                        <tr>
                          <th>{renderSortableHeader('Month', historySort.key === 'month', historySort.direction, () => handleHistorySort('month'))}</th>
                          <th>{renderSortableHeader('Date', historySort.key === 'date', historySort.direction, () => handleHistorySort('date'))}</th>
                          <th>{renderSortableHeader('Amount', historySort.key === 'amount', historySort.direction, () => handleHistorySort('amount'))}</th>
                          <th>{renderSortableHeader('Status', historySort.key === 'status', historySort.direction, () => handleHistorySort('status'))}</th>
                          <th>{renderSortableHeader('Method', historySort.key === 'method', historySort.direction, () => handleHistorySort('method'))}</th>
                          <th>{renderSortableHeader('Source', historySort.key === 'source', historySort.direction, () => handleHistorySort('source'))}</th>
                          <th>{renderSortableHeader('Donation ID', historySort.key === 'donationId', historySort.direction, () => handleHistorySort('donationId'))}</th>
                          <th>{renderSortableHeader('Transaction ID', historySort.key === 'transactionId', historySort.direction, () => handleHistorySort('transactionId'))}</th>
                          <th>{renderSortableHeader('Note', historySort.key === 'note', historySort.direction, () => handleHistorySort('note'))}</th>
                          <th>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredSortedHistory.length ? filteredSortedHistory.map((row: NityaSevaDonationHistoryItem) => {
                          const rowStatus = row.kind === 'due' ? 'Due' : (row.donation?.status || row.status || '—');
                          return (
                            <tr key={`${row.kind || 'paid'}-${row.month || row.donation?.id || row.donation?.createdAt || row.donation?.date}`}>
                              <td>{row.month ? dayjs(row.month).format('MMM YYYY') : (row.donation?.date ? dayjs(row.donation.date).format('MMM YYYY') : '—')}</td>
                              <td>{row.kind === 'due' ? '—' : (row.displayDate ? dayjs(row.displayDate).format('D MMM YYYY') : (row.donation?.date ? dayjs(row.donation.date).format('D MMM YYYY') : '—'))}</td>
                              <td>₹{Number(row.amount || row.donation?.amount || 0).toFixed(0)}</td>
                              <td>
                                <Chip size="sm" variant="soft" color={statusColor(rowStatus)}>{rowStatus}</Chip>
                              </td>
                              <td>{row.kind === 'due' ? '—' : (row.donation?.paymentMethod || row.paymentMethod || '—')}</td>
                              <td>{row.kind === 'due' ? 'Due' : (row.subscription?.id ? 'Subscription' : 'Manual')}</td>
                              <td>
                                <Typography level="body-sm" sx={{ fontFamily: 'monospace' }}>
                                  {row.kind === 'due' ? '—' : (row.donation?.id || '—')}
                                </Typography>
                              </td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    fontFamily: 'monospace',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    display: 'block',
                                  }}
                                  title={row.kind === 'due' ? '—' : (row.donation?.txnRefId || row.subscription?.razorpayId || '—')}
                                >
                                  {row.kind === 'due' ? '—' : (row.donation?.txnRefId || row.subscription?.razorpayId || '—')}
                                </Typography>
                              </td>
                              <td>
                                <Typography
                                  level="body-sm"
                                  sx={{
                                    display: '-webkit-box',
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                  title={row.kind === 'due' ? normalizeDueNote(row.dueReason) : (row.donation?.note || '—')}
                                >
                                  {row.kind === 'due' ? normalizeDueNote(row.dueReason) : (row.donation?.note || '—')}
                                </Typography>
                              </td>
                              <td style={{ whiteSpace: 'nowrap' }}>
                                {row.kind === 'due' ? (
                                  <Button size="sm" variant="soft" startDecorator={<FavoriteRounded fontSize="small" />} onClick={() => openRecordDonationModal(row)}>
                                    Record
                                  </Button>
                                ) : row.donation?.id ? (
                                  <Stack direction="row" spacing={1}>
                                    <Button
                                      size="sm"
                                      variant="soft"
                                      color="neutral"
                                      startDecorator={<EditRounded fontSize="small" />}
                                      onClick={() => { void openEditDonationModal(row); }}
                                    >
                                      Edit
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="soft"
                                      startDecorator={<ReceiptLong fontSize="small" />}
                                      onClick={() => downloadReceipt(Number(row.donation?.id))}
                                    >
                                      Receipt
                                    </Button>
                                  </Stack>
                                ) : (
                                  '—'
                                )}
                              </td>
                            </tr>
                          );
                        }) : (
                          <tr>
                            <td colSpan={10}><Typography level="body-sm">No Nitya Seva donations found for the selected filter.</Typography></td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </Box>
                </Sheet>
              </CardContent>
            </Card>
          </Stack>
        ) : null}

        <Modal open={Boolean(manualTargetRow)} onClose={() => { if (!savingManualDonation) closeManualDonationModal(); }}>
          <ModalDialog sx={{ width: 'min(94vw, 560px)' }}>
            <ModalClose disabled={savingManualDonation} />
            <Typography level="title-lg">{manualMode === 'edit' ? 'Edit Donation' : 'Record Manual Donation'}</Typography>
            <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
              {manualTargetRow?.month
                ? `${manualMode === 'edit' ? 'Update' : 'For'} ${dayjs(manualTargetRow.month).format('MMM YYYY')} • ${getPaymentFrequencyLabel(manualPaymentFrequency)} cycle`
                : (manualMode === 'edit' ? 'Update recorded donation' : 'Record donation for selected due month')}
            </Typography>
            {savingManualDonation ? (
              <Alert color="primary" variant="soft">
                {manualMode === 'edit'
                  ? 'Updating donation and refreshing the history. Please wait...'
                  : 'Recording donation and refreshing the history. Please wait...'}
              </Alert>
            ) : null}
            {detailData?.enrollment ? (
              <Alert color="primary" variant="soft">
                Monthly pledge: ₹{Number(detailData.enrollment.pledgeAmount || 0).toLocaleString('en-IN')} • Suggested cycle amount: ₹{getCycleAmount(Number(detailData.enrollment.pledgeAmount || 0), manualPaymentFrequency).toLocaleString('en-IN')}
              </Alert>
            ) : null}
            <FormControl>
              <FormLabel>Payment Frequency</FormLabel>
              <RadioGroup
                value={manualPaymentFrequency}
                onChange={(e) => {
                  const nextFrequency = e.target.value as NityaSevaPaymentFrequency;
                  setManualPaymentFrequency(nextFrequency);
                  if (detailData?.enrollment) {
                    setManualAmount(String(getCycleAmount(Number(detailData.enrollment.pledgeAmount || 0), nextFrequency)));
                  }
                }}
              >
                {paymentFrequencyOptions.map((option) => (
                  <Radio
                    key={option.value}
                    value={option.value}
                    disabled={savingManualDonation}
                    label={`${option.label} (${option.months === 1 ? 'pay every month' : `pay once for ${option.months} months`})`}
                  />
                ))}
              </RadioGroup>
            </FormControl>
            <Input disabled={savingManualDonation} type="number" placeholder="Amount" value={manualAmount} onChange={(e) => setManualAmount(e.target.value)} />
            <Input disabled={savingManualDonation} type="month" value={manualForMonth} onChange={(e) => setManualForMonth(e.target.value)} />
            <Input disabled={savingManualDonation} type="date" value={manualDate} onChange={(e) => setManualDate(e.target.value)} />
            <Input disabled={savingManualDonation} placeholder="Transaction ID (optional)" value={manualTransactionId} onChange={(e) => setManualTransactionId(e.target.value)} />
            <Select disabled={savingManualDonation} value={manualMethod} onChange={(_, val) => setManualMethod((val as string) || 'Cash')}>
              <Option value="Cash">Cash</Option>
              <Option value="Online">Online</Option>
              <Option value="Bank Transfer">Bank Transfer</Option>
            </Select>
            <Textarea disabled={savingManualDonation} minRows={3} placeholder="Note (optional)" value={manualNote} onChange={(e) => setManualNote(e.target.value)} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button variant="plain" disabled={savingManualDonation} onClick={closeManualDonationModal}>
                Cancel
              </Button>
              <Button
                loading={savingManualDonation}
                onClick={async () => {
                  if (!detailData?.enrollment?.id || !manualTargetRow) return;
                  setSavingManualDonation(true);
                  setError('');
                  try {
                    const requestPayload = {
                      amount: Number(manualAmount),
                      date: manualDate,
                      forMonth: manualForMonth,
                      paymentFrequency: manualPaymentFrequency,
                      paymentMethod: manualMethod,
                      transactionId: manualTransactionId,
                      note: manualNote,
                    };

                    if (manualMode === 'edit' && editingDonationSnapshot?.id) {
                      await updateDonationById(Number(editingDonationSnapshot.id), {
                        ...editingDonationSnapshot,
                        amount: requestPayload.amount,
                        date: dayjs(requestPayload.date).format('YYYY-MM-DDT12:00:00Z'),
                        paymentMethod: requestPayload.paymentMethod,
                        txnRefId: requestPayload.transactionId,
                        note: requestPayload.note,
                        subPurpose: buildNityaSevaCoverageMeta(requestPayload.forMonth, requestPayload.paymentFrequency),
                      });
                    } else {
                      await recordNityaSevaManualDonation(detailData.enrollment.id, {
                        amount: requestPayload.amount,
                        date: requestPayload.date,
                        forMonth: requestPayload.forMonth,
                        paymentFrequency: requestPayload.paymentFrequency,
                        paymentMethod: requestPayload.paymentMethod,
                        transactionId: requestPayload.transactionId,
                        note: requestPayload.note,
                      });
                      applyOptimisticManualDonation(requestPayload);
                      try {
                        const latestDetail = await fetchNityaSevaEnrollmentDetail(detailData.enrollment.id);
                        const latestHistory = Array.isArray(latestDetail?.donationHistory) ? latestDetail.donationHistory : [];
                        const hasDue = latestHistory.some((row) => row?.kind === 'due');
                        const hasStartedDonating = latestHistory.some((row) => row?.kind !== 'due' && (row?.donation?.id || Number(row?.amount || 0) > 0));

                        if (hasStartedDonating && !hasDue && latestDetail?.enrollment?.status !== 'active') {
                          await updateNityaSevaEnrollment(detailData.enrollment.id, { status: 'active' });
                          setDetailData((prev) => {
                            if (!prev) {
                              return prev;
                            }
                            return {
                              ...prev,
                              enrollment: {
                                ...prev.enrollment,
                                status: 'active',
                                updatedAt: new Date().toISOString(),
                              },
                            };
                          });
                        }
                      } catch (_statusErr) {
                        // Status promotion is best-effort and should not block donation recording.
                      }
                    }
                    closeManualDonationModal();
                    await refreshDetailAfterMutation();
                  } catch (_saveErr) {
                    setError(manualMode === 'edit' ? 'Failed to update donation. Please try again.' : 'Failed to record manual donation. Please try again.');
                  } finally {
                    setSavingManualDonation(false);
                  }
                }}
              >
                {manualMode === 'edit' ? 'Update Donation' : 'Save Donation'}
              </Button>
            </Stack>
          </ModalDialog>
        </Modal>
      </Box>
    </Box>
  );
}